'use client';

import Image, { type StaticImageData } from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import AwsStampImage from '@/assets/images/AwsStamp.png';
import CloudflareImage from '@/assets/images/CloudflareLogo.png';
import LgpdStampImage from '@/assets/images/LgpdStamp.png';
import SafeBuyStampImage from '@/assets/images/SafeBuyStamp.png';
import SslStampImage from '@/assets/images/SslStamp.png';
import { useWhitelabelConfig } from '@/hooks/useWhitelabelConfig';
import { cn, dayjs } from '@/libs';
import type { FooterData } from '@/services/footerService';
import {
  BeneficiaryKey,
  BrandingKey,
  CapitalizerKey,
  InstitutionalKey,
  PartnershipKey,
} from '@/types/enums/WhitelabelConfig';
import type { WhitelabelConfigValue } from '@/types/models/WhitelabelConfig';
import { parsePartnershipItems } from '@/utils/partnerships';

import { FooterPartnerships } from './FooterPartnerships';

import { Icon, Separator } from '../ui';

type FooterProps = {
  footerText?: string;
  whitelabelAboutUs?: string;
  privacyPolicyLink?: string;
  capitalizerLink?: string;
  wlName?: string;
  ilotoLink?: string;
  benefitedInstitution?: string;
  termsOfUseLink?: string;
  generalConditionsLink?: string;
  responsibleGamingLink?: string;
  instagramUrl?: string;
  benefited?: FooterData['benefited'];
  capitalizer?: FooterData['capitalizer'];
};

const FOOTER_IMAGES: { name: string; image: StaticImageData }[] = [
  { name: 'Cloudflare', image: CloudflareImage },
  { name: 'AWS Certified', image: AwsStampImage },
  { name: 'Safe Buy', image: SafeBuyStampImage },
  { name: 'LGPD', image: LgpdStampImage },
  { name: 'SSL', image: SslStampImage },
];

export function Footer({
  footerText = '',
  whitelabelAboutUs = '',
  capitalizerLink = '',
  wlName = '',
  ilotoLink = '',
  privacyPolicyLink = '',
  benefitedInstitution = '',
  termsOfUseLink = '',
  generalConditionsLink = '',
  responsibleGamingLink = '',
  instagramUrl = '',
  benefited,
  capitalizer,
}: FooterProps) {
  const { getValue } = useWhitelabelConfig();
  const pathname = usePathname();

  const addPaddingBottom = pathname === '/' || pathname.includes('/detalhes');
  const partnerships = parsePartnershipItems(
    getValue<WhitelabelConfigValue>(PartnershipKey.PARTNERSHIP_ITEMS),
  );
  const partnershipLabel = getValue(PartnershipKey.PARTNERSHIP_LABEL) ?? '';

  return (
    <footer
      data-cy="footer"
      className={cn(
        'mt-auto bg-footer w-screen py-4 md:pb-16 text-background relative left-1/2 right-1/2 -mx-[50vw]',
        addPaddingBottom && 'pb-80 md:pb-20',
      )}
    >
      <div className="max-w-screen-xl mx-auto px-4">
        <div
          className={cn(
            'mb-6 xl:flex xl:flex-row xl:gap-14',
            !whitelabelAboutUs && 'xl:justify-center',
          )}
        >
          <div
            className={cn(
              'contents xl:block xl:mb-5',
              whitelabelAboutUs ? 'xl:w-[46%]' : 'xl:w-auto',
            )}
          >
            <div className="flex flex-row justify-between gap-4 xl:contents">
              <div className="shrink-0 max-w-[50%] xl:max-w-none">
                <h3 className="mb-2 text-primary-foreground text-xs md:text-2xl font-bold">
                  {getValue(InstitutionalKey.INSTITUTIONAL_LABEL_ABOUT_US) ||
                    'Canal de Vendas:'}
                </h3>
                <div className="mb-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-2xl font-bold">
                      <Image
                        src={getValue(BrandingKey.BRANDING_LOGO_FOOTER) ?? ''}
                        alt={`Logo ${getValue(BrandingKey.BRANDING_NAME) ?? ''}`}
                        width={120}
                        height={32}
                        className="mx-auto"
                      />
                    </div>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <FooterPartnerships
                label={partnershipLabel}
                items={partnerships}
                className="min-w-0 mb-5 xl:hidden"
              />
            </div>

            {whitelabelAboutUs && (
              <div className="mb-5 text-justify text-primary-foreground text-sm leading-relaxed xl:mb-0">
                <div dangerouslySetInnerHTML={{ __html: whitelabelAboutUs }} />
              </div>
            )}
          </div>

          <div
            className={cn(
              'flex flex-row justify-between',
              whitelabelAboutUs ? 'xl:w-[54%]' : 'gap-16',
            )}
          >
            <div>
              <h3 className="mb-2 text-primary-foreground text-xs md:text-2xl font-bold">
                {getValue(CapitalizerKey.CAPITALIZER_LABEL_CAPITALIZER) || 'Realização:'}
              </h3>
              <Link
                href={
                  capitalizer?.link ||
                  (getValue(CapitalizerKey.CAPITALIZER_LINK) ?? '') ||
                  capitalizerLink ||
                  '#'
                }
              >
                <Image
                  src={
                    capitalizer?.logo ||
                    (getValue(CapitalizerKey.CAPITALIZER_LOGO_FOOTER) ?? '')
                  }
                  alt={
                    capitalizer?.name ||
                    (getValue(CapitalizerKey.CAPITALIZER_DISPLAY_NAME) ?? '')
                  }
                  width={100}
                  height={100}
                  style={{ width: '120px', height: '50px', objectFit: 'contain' }}
                />
              </Link>
            </div>
            {(benefited?.logo || (getValue(BeneficiaryKey.BENEFICIARY_LOGO) ?? '')) && (
              <div>
                <h3 className="mb-2 text-primary-foreground text-xs md:text-2xl font-bold">
                  {getValue(BeneficiaryKey.BENEFICIARY_LABEL) ||
                    'Instituição Beneficiada:'}
                </h3>
                <Link
                  href={
                    benefited?.link ||
                    (getValue(BeneficiaryKey.BENEFICIARY_LINK) ?? '') ||
                    benefitedInstitution ||
                    '#'
                  }
                >
                  <Image
                    src={
                      benefited?.logo || (getValue(BeneficiaryKey.BENEFICIARY_LOGO) ?? '')
                    }
                    alt={
                      benefited?.name || (getValue(BeneficiaryKey.BENEFICIARY_NAME) ?? '')
                    }
                    width={100}
                    height={100}
                    style={{ width: '120px', height: '50px', objectFit: 'contain' }}
                  />
                </Link>
              </div>
            )}
          </div>
        </div>

        <FooterPartnerships
          label={partnershipLabel}
          items={partnerships}
          className="hidden xl:block"
        />

        {footerText && (
          <div className="text-primary-foreground text-justify mb-4 text-xxs md:text-xs leading-relaxed">
            <div dangerouslySetInnerHTML={{ __html: footerText }} />
          </div>
        )}

        <Separator className="mb-4" />

        <div className="flex flex-col md:flex-row items-start md:justify-between md:gap-2">
          <div className="flex flex-col items-start w-full md:w-auto text-left gap-0 md:gap-2 md:mb-0">
            <p className="text-primary-foreground/90 text-xxs md:text-sm mb-1 md:mb-0">
              {(wlName || getValue(BrandingKey.BRANDING_NAME)) ?? ''} © {dayjs().year()} -
              Todos os direitos reservados.
            </p>
            <Link
              data-cy="footer-privacy-policy-link"
              href={privacyPolicyLink}
              className="inline-flex items-center min-h-7 md:min-h-0 text-sm text-primary-foreground/90 underline cursor-pointer hover:text-primary-foreground transition-colors text-left"
            >
              Política de Privacidade
            </Link>
            <Link
              data-cy="footer-terms-of-use-link"
              href={termsOfUseLink}
              className="inline-flex items-center min-h-7 md:min-h-0 text-sm text-primary-foreground/90 underline cursor-pointer hover:text-primary-foreground transition-colors text-left"
            >
              Termos de Uso
            </Link>
            {generalConditionsLink && (
              <Link
                data-cy="footer-general-conditions-link"
                href={generalConditionsLink}
                className="inline-flex items-center min-h-7 md:min-h-0 text-sm text-primary-foreground/90 underline cursor-pointer hover:text-primary-foreground transition-colors text-left"
              >
                Condições Gerais
              </Link>
            )}
            {responsibleGamingLink && (
              <Link
                data-cy="footer-responsible-gaming-link"
                href={responsibleGamingLink}
                className="inline-flex items-center min-h-7 md:min-h-0 text-sm text-primary-foreground/90 underline cursor-pointer hover:text-primary-foreground transition-colors text-left"
              >
                Jogo Responsável
              </Link>
            )}
            {instagramUrl && (
              <Link
                data-cy="footer-instagram-link"
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex items-center min-h-7 md:min-h-0 gap-2 text-sm text-primary-foreground/90 cursor-pointer hover:text-primary-foreground transition-colors text-left"
              >
                <Icon.Instagram width={22} height={22} />
                <span>Instagram</span>
              </Link>
            )}
          </div>

          <div className="flex w-full md:w-auto items-center justify-center pt-3 md:pt-2">
            <div className="flex md:flex-row flex-col items-center justify-center gap-1">
              <span className="text-primary-foreground/90 text-xxs md:text-sm">
                Desenvolvido por{' '}
              </span>
              <Link
                href={ilotoLink}
                className="text-primary-foreground/90 hover:text-white transition-colors"
              >
                <Icon.IlottoLogo width={100} height={40} />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-6 md:mt-4 flex items-center justify-around md:justify-end md:gap-3">
          {FOOTER_IMAGES.map(({ image, name }) => (
            <Image
              key={name}
              alt={name}
              src={image}
              width={50}
              height={50}
              className="object-contain w-14 h-14"
            />
          ))}
        </div>
      </div>
    </footer>
  );
}
